import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { useUpdateKitRequestsMutation } from 'store/slices/clientV2/kitRequests';
import { GridLegacy, Stack, Typography } from '@mui/material';
import {
  formatToShortDate,
  convertDate,
  formatToUniversalDate,
} from 'utils/dates';
import {
  HookFormDatePicker,
  HookFormToggle,
} from 'ui-component/HookFormComponents';
import useSnackbar from 'hooks/useSnackbar';
import { useTypedForm } from 'hooks/useTypedForm';
import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';
import {
  EstimatedShipDateResult,
  EstimatedShipDateAlert,
} from 'hooks/useEstimateKitShipDate';
import {
  formConstants,
  estimatedShipDateValidationSchema,
  EstimatedShipDateValidFormData,
} from 'views/kitting/Kit/components/EstimatedShipDateDialog/constants';
import { useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';

type EstimatedShipDateDialogProps = {
  open: boolean;
  onClose: () => void;
  kitRequest: KitRequestSubtype;
  estimatedShipDateData: EstimatedShipDateResult;
};

const EstimatedShipDateDialog = ({
  open,
  onClose,
  kitRequest,
  estimatedShipDateData,
}: EstimatedShipDateDialogProps) => {
  const [updateKitRequest, { isLoading }] = useUpdateKitRequestsMutation();
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const { handleSubmit, control, setValue } = useTypedForm(
    estimatedShipDateValidationSchema,
    {
      defaultValues: {
        [formConstants.targetShipDate.id]:
          (estimatedShipDateData.estimatedShipDate &&
            convertDate(estimatedShipDateData.estimatedShipDate)) ||
          null,
        [formConstants.shipPartial.id]: kitRequest.shipPartial,
      },
    }
  );

  const watchShipPartial = useWatch({
    control,
    name: formConstants.shipPartial.id,
  })!;

  const minDate = useMemo(
    () =>
      watchShipPartial
        ? (estimatedShipDateData.estimatedProcessingDate &&
            convertDate(estimatedShipDateData.estimatedProcessingDate)) ||
          null
        : (estimatedShipDateData.estimatedShipDate &&
            convertDate(estimatedShipDateData.estimatedShipDate)) ||
          null,
    [
      watchShipPartial,
      estimatedShipDateData.estimatedShipDate,
      estimatedShipDateData.estimatedProcessingDate,
    ]
  );

  useEffect(() => {
    setValue(formConstants.targetShipDate.id, minDate);
  }, [watchShipPartial, minDate]);

  const onSubmit = async (data: EstimatedShipDateValidFormData) => {
    try {
      const targetShipDate = data[formConstants.targetShipDate.id];
      const formattedTargetShipDate = targetShipDate
        ? formatToUniversalDate(targetShipDate)
        : undefined;
      await updateKitRequest({
        id: kitRequest.id,
        targetShipDate: formattedTargetShipDate,
        estimatedShipDate: estimatedShipDateData.estimatedShipDate,
        shipPartial: data[formConstants.shipPartial.id],
      }).unwrap();

      dispatchSuccessSnackbar('Estimated ship date updated successfully');
      onClose();
    } catch (error) {
      dispatchErrorSnackbar(error as string);
    }
  };

  return (
    <ExtendedDialog
      title="Update Estimated Ship Date"
      open={open}
      onCloseDialog={onClose}
      maxWidth="md"
      permissionScope={{
        app: ALL_APPS.KITTING.id,
        accessLevel: appPermissionAccessLevels.edit,
      }}
      isForm
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isLoading}
      submitButtonCopy="Update"
    >
      <Stack spacing={3}>
        <EstimatedShipDateAlert
          estimatedShipDate={estimatedShipDateData.estimatedShipDate}
          expectedByDate={
            estimatedShipDateData.latestExpectedStockLot?.expectedBy || null
          }
          processingDate={estimatedShipDateData.estimatedProcessingDate}
          kitRequestEstimatedShipDate={kitRequest.estimatedShipDate}
          kitRequestTargetShipDate={kitRequest.targetShipDate}
          shipPartial={watchShipPartial}
        />
        <GridLegacy container spacing={2}>
          <GridLegacy item xs={6}>
            <Typography variant="subtitle1">New Estimated Date:</Typography>
            <Typography variant="body1" fontWeight="bold">
              {estimatedShipDateData.estimatedShipDate &&
                formatToShortDate(estimatedShipDateData.estimatedShipDate)}
            </Typography>
          </GridLegacy>
          <GridLegacy item xs={6}>
            <Typography variant="subtitle1">
              Old Estimated Ship Date:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {kitRequest.estimatedShipDate &&
                formatToShortDate(kitRequest.estimatedShipDate)}
            </Typography>
          </GridLegacy>
        </GridLegacy>
        <HookFormToggle
          name={formConstants.shipPartial.id}
          label={formConstants.shipPartial.label}
          control={control}
        />
        <HookFormDatePicker
          name={formConstants.targetShipDate.id}
          label={formConstants.targetShipDate.label}
          control={control}
          minDate={minDate}
          fullWidth
        />
      </Stack>
    </ExtendedDialog>
  );
};

export default EstimatedShipDateDialog;
